import {
  ColumnDefinition,
  HookState,
  RelativeTimeCellData,
  RowDataBase,
  StatusChipCellData,
  TokenAmountCellData,
  TokenWithLabelCellData,
  useChainTokenBalances,
  Web3Context,
} from 'common-client';
import React, { useContext, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AuctionTable } from '../components/AuctionTable';
import { ConfigContext } from '../config';
import { Auction } from '../entities/Auction';
import { useAuctions } from '../hooks/useAuctions';
import { useAuctionTokens } from '../hooks/useAuctionTokens';
import { updateURLSearchParams } from '../utils/updateURLSearchParams';

const dataKey: unique symbol = Symbol('AuctionTableRowDataSubDataKey');

interface AuctionTableWrapperProps {
  /**
   * Callback when an auction row is selected
   * @param auction
   */
  onAuctionSelect?: (auction: Auction) => void;
  /**
   * Callback to initiate create auction flow
   */
  onCreateAuction?: () => void;
}

interface RowData extends RowDataBase {
  [dataKey]: Auction;
  askAsset: TokenWithLabelCellData;
  bidAsset: TokenWithLabelCellData;
  status: StatusChipCellData;
  askBalance: TokenAmountCellData;
  bidBalance: TokenAmountCellData;
  endingIn: RelativeTimeCellData;
}

const columns: ColumnDefinition<RowData>[] = [
  {
    id: 'askAsset',
    type: 'token with label',
    title: 'Sell Asset',
    // tooltip: 'TODO',
    sortable: true,
  },
  {
    id: 'bidAsset',
    type: 'token with label',
    title: 'Bid Asset',
    // tooltip: 'TODO',
    sortable: true,
  },
  {
    id: 'status',
    type: 'status chip',
    title: 'Status',
    // tooltip: 'TODO',
    sortable: true,
  },
  {
    id: 'askBalance',
    type: 'token amount',
    title: 'Total Sell Asset Deposits',
    // tooltip: 'TODO',
    sortable: true,
  },
  {
    id: 'bidBalance',
    type: 'token amount',
    title: 'Total Bid Asset Deposits',
    // tooltip: 'TODO',
    sortable: true,
  },
  {
    id: 'endingIn',
    type: 'relative time',
    title: 'Ending In',
    // tooltip: 'TODO',
    sortable: true,
  },
];

const sortColumnIds = columns.filter((column) => column.sortable).map((column) => column.id);

const sortColumnIdToOrderByMap: Record<string, string> = {
  askAsset: 'askAssetSymbol',
  bidAsset: 'bidAssetSymbol',
  status: 'settled',
  askBalance: 'askBalance',
  bidBalance: 'bidBalance',
  endingIn: 'endDate',
};

const filterToOnlyActiveMap: Record<string, boolean> = {
  All: false,
  Active: true,
  'My Assets': true,
  'My Auctions': false,
};

const sortLabels = {
  'askAsset-ascending': null,
  'askAsset-descending': null,
  'bidAsset-ascending': null,
  'bidAsset-descending': null,
  'status-ascending': 'Sort by Active First',
  'status-descending': 'Sort by Settled First',
  'askBalance-ascending': null,
  'askBalance-descending': null,
  'bidBalance-ascending': null,
  'bidBalance-descending': null,
  'endingIn-ascending': 'Sort by Ending Soonest',
  'endingIn-descending': 'Sort by Ending Latest',
};

const rowsPerPage = 10;

export function AuctionTableWrapper({
  onAuctionSelect,
  onCreateAuction,
}: AuctionTableWrapperProps) {
  const { address } = useContext(Web3Context);
  const { search } = useLocation();
  const history = useHistory();
  const { networkConfig } = useContext(ConfigContext);

  const filters = ['All', 'Active', 'My Assets', 'My Auctions'];
  const defaultValues = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    const searchText = searchParams.get('searchText') || '';
    let currentFilter = searchParams.get('currentFilter');
    if (!currentFilter || !filters.includes(currentFilter)) {
      [, currentFilter] = filters;
    }
    let sortColumnId = searchParams.get('sortColumnId');
    if (!sortColumnId || !sortColumnIds.includes(sortColumnId)) {
      sortColumnId = 'endingIn';
    }
    const sortAscending = searchParams.get('sortAscending') !== 'false';
    return {
      searchText,
      currentFilter,
      sortColumnId,
      sortAscending,
    };
  }, []);
  const [searchText, setSearchText] = useState<string>(defaultValues.searchText);
  const [currentFilter, setCurrentFilter] = useState<string>(defaultValues.currentFilter);
  const [sortColumnId, setSortColumnId] = useState<string>(defaultValues.sortColumnId);
  const [sortAscending, setSortAscending] = useState<boolean>(defaultValues.sortAscending);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

  const auctionTokensHook = useAuctionTokens();
  const auctionTokens = useMemo(() => auctionTokensHook.output || [], [auctionTokensHook.output]);
  const tokenBalancesHook = useChainTokenBalances(auctionTokens);
  const myAssetsAddresses = useMemo(() => {
    if (tokenBalancesHook.output) {
      return Array.from(tokenBalancesHook.output.values())
        .filter(((balance) => balance.greaterThan(0)))
        .map((balance) => balance.currency.address);
    }
    return [];
  }, [tokenBalancesHook]);
  const auctionsHook = useAuctions({
    orderBy: sortColumnIdToOrderByMap[sortColumnId],
    orderAscending: sortAscending,
    tokenNameSubstring: searchText.length ? searchText : undefined,
    assets: currentFilter === 'My Assets' ? myAssetsAddresses : undefined,
    // Pass zero address so that the hook return empty array when this filter is selected but
    //   the user hasn't connected their wallet. Passing undefined skips the filter instead.
    accountAddress: currentFilter === 'My Auctions' ? (address || '0x0000000000000000000000000000000000000000') : undefined,
    onlyActive: filterToOnlyActiveMap[currentFilter],
  }, 30000);
  const rowData: RowData[] = useMemo(() => {
    if (auctionsHook.state === HookState.SUCCESS && auctionsHook.output) {
      return auctionsHook.output.map((auction) => ({
        [dataKey]: auction,
        askAsset: {
          token: auction.askAsset,
          label: 'Sell Asset',
        },
        bidAsset: {
          token: auction.bidAsset,
          label: 'Bid Asset',
        },
        status: {
          mode: auction.settled ? 'error' : 'success',
          label: auction.settled ? 'Settled' : 'Active',
        },
        askBalance: {
          value: auction.askBalance,
        },
        bidBalance: {
          value: auction.bidBalance,
        },
        endingIn: {
          value: (auction.endDate.valueOf() - Date.now()) / 1000,
        },
      }));
    }
    return [];
  }, [auctionsHook]);
  const totalPageCount = Math.ceil(rowData.length / rowsPerPage);
  // currentPageIndex can be higher than available pages if changing filter or so forth
  const coercedCurrentPageIndex = Math.max(Math.min(totalPageCount - 1, currentPageIndex), 0);
  const rowDataDisplayed = rowData.slice(
    coercedCurrentPageIndex * rowsPerPage,
    (coercedCurrentPageIndex + 1) * rowsPerPage,
  );

  const onRowSelect = (data: RowData) => {
    onAuctionSelect?.(data[dataKey]);
  };
  const onFilterSelect = (currentFilterNew: string) => {
    setCurrentFilter(currentFilterNew);
    updateURLSearchParams(history, search, { key: 'currentFilter', value: currentFilterNew });
  };
  const onSortChange = (sortColumnIdNew: string, sortAscendingNew: boolean) => {
    setSortColumnId(sortColumnIdNew);
    setSortAscending(sortAscendingNew);
    updateURLSearchParams(history, search, [
      { key: 'sortColumnId', value: sortColumnIdNew },
      { key: 'sortAscending', value: sortAscendingNew },
    ]);
  };
  const onPageChange = (newPageIndex: number) => {
    setCurrentPageIndex(newPageIndex);
  };
  const onSearchTextChange = (searchTextNew: string) => {
    setSearchText(searchTextNew);
    updateURLSearchParams(history, search, { key: 'searchText', value: searchTextNew });
  };

  return (
    <AuctionTable
      columns={columns}
      rowData={rowDataDisplayed}
      onRowSelect={onRowSelect}
      filters={filters}
      currentFilter={currentFilter}
      onFilterSelect={onFilterSelect}
      sortColumnId={sortColumnId}
      sortAscending={sortAscending}
      sortLabels={sortLabels}
      onSortChange={onSortChange}
      currentPageIndex={coercedCurrentPageIndex}
      totalPageCount={totalPageCount}
      onPageChange={onPageChange}
      searchText={searchText}
      setSearchText={onSearchTextChange}
      createAuctionDisabled={!!networkConfig?.createAuctionDisabled}
      onCreateAuction={onCreateAuction}
    />
  );
}
